import React, { useRef } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import SectionWrapper from "../components/common/SectionWrapper";
import ServiceCard from "../components/common/ServiceCard";
import Button from "../components/common/Button";
import Hero from "../components/Hero/Hero";
import useScrollAnimation from "../hooks/useScrollAnimation";
import styles from "./Home.module.css";
import { servicesData } from "../data/servicesData";
import {
  FaStethoscope,
  FaUserMd,
  FaXRay,
  FaFlask,
  FaCut,
  FaHeartbeat,
  FaHospitalUser,
  FaComments,
  FaClipboardList,
  FaHandHoldingHeart,
} from "react-icons/fa";

const Home = () => {
  // Create a ref for the services section to scroll to
  const servicesRef = useRef(null);
  const scrollToServices = () => {
    servicesRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const servicesRefAnim = useScrollAnimation("animate-fade-up", 0.2);
  const doctorRef = useScrollAnimation("animate-fade-left", 0.2);
  const whyRef = useScrollAnimation("animate-fade-up", 0.2);
  const ctaRef = useScrollAnimation("animate-fade-up", 0.2);

  const featuredServices = servicesData.slice(0, 6);

  const whyUs = [
    {
      icon: FaHospitalUser,
      title: "Experienced Leadership",
      description: "Led by Dr. Kimeu with 15+ years in healthcare management.",
    },
    {
      icon: FaComments,
      title: "Telemedicine Ready",
      description: "Consult via WhatsApp, phone, or chat – from anywhere.",
    },
    {
      icon: FaClipboardList,
      title: "Comprehensive Services",
      description:
        "From general consultations to specialized care and wellness programs.",
    },
    {
      icon: FaHandHoldingHeart,
      title: "Patient-Centered Care",
      description: "Personalized treatment plans and compassionate support.",
    },
  ];

  return (
    <>
      <Helmet>
        <title>Muigai Medical Center – Your Health, Our Priority</title>
        <meta
          name="description"
          content="Muigai Medical Center provides expert healthcare, telemedicine services, diagnostics, and wellness programs. Led by Dr. Jack Kimeu for quality patient care."
        />
      </Helmet>

      {/* Hero Section */}
      <Hero onScrollToServices={scrollToServices} />

      {/* Services Section with Parallax Background */}
      <div className={styles.parallaxSection}>
        <div className={styles.parallaxOverlay}></div>
        <div className={`container ${styles.parallaxContainer}`}>
          <div ref={servicesRef}>
            <SectionWrapper
              title="Our Services"
              subtitle="Comprehensive care under one roof"
              bg="transparent"
            >
              <div ref={servicesRefAnim} className={styles.servicesGrid}>
                {featuredServices.map((service, idx) => (
                  <div
                    key={service.id}
                    className={`${styles.serviceItem} animate-fade-up delay-${
                      (idx % 5) * 100
                    }`}
                  >
                    <ServiceCard
                      id={service.id}
                      title={service.title}
                      description={service.shortDesc}
                      icon={service.icon}
                      bgImage={service.bgImage} // ✅ pass the background image
                    />
                  </div>
                ))}
              </div>
              <div className={styles.textCenter}>
                <Link to="/services">
                  <Button variant="secondary">View All Services</Button>
                </Link>
              </div>
            </SectionWrapper>
          </div>
        </div>
      </div>

      {/* Doctor Snapshot */}
      <SectionWrapper bg="light" title="Meet Jack Nguthi Kimeu">
        <div ref={doctorRef} className={styles.doctorContainer}>
          <div className={styles.doctorImage}>
            <img src="/assets/doctor.jpg" alt="Dr. Jack Kimeu" />
          </div>
          <div className={styles.doctorInfo}>
            <h3>Clinical Medicine & Organizational Development Expert</h3>
            <p>
              With over 15 years of experience, Kimeu blends clinical expertise
              with strategic healthcare leadership. He holds a Master's in
              Organizational Development, a Bachelor's in Counseling Psychology,
              and a Bachelor in Clinical Medicine & Surgery from Egerton
              University 2004 to 2006.
            </p>
            <ul className={styles.doctorHighlights}>
              <li>✓ Led health centers with high patient satisfaction</li>
              <li>
                ✓ Implemented EHR systems reducing documentation errors by 20%
              </li>
              <li>✓ Passionate about employee wellness and preventive care</li>
              <li>✓ Mentor to junior medical staff</li>
            </ul>
            <Link to="/about">
              <Button variant="primary">Learn More</Button>
            </Link>
          </div>
        </div>
      </SectionWrapper>

      {/* Why Choose Us */}
      <SectionWrapper title="Why Choose Muigai Medical Center">
        <div ref={whyRef} className={styles.whyGrid}>
          {whyUs.map((item, idx) => (
            <div
              key={idx}
              className={`${styles.whyItem} animate-fade-up delay-${idx * 100}`}
            >
              <div className={styles.whyIcon}>
                <item.icon />
              </div>
              <h3>{item.title}</h3>
              <p>{item.description}</p>
            </div>
          ))}
        </div>
      </SectionWrapper>

      {/* CTA Banner */}
      <section ref={ctaRef} className={styles.cta}>
        <div className="container">
          <h2>Ready to take charge of your health?</h2>
          <p>Book an appointment or consult online today.</p>
          <Link to="/appointment">
            <Button variant="primary">Book Now</Button>
          </Link>
        </div>
      </section>
    </>
  );
};

export default Home;
